import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, Text, Group, Button, Table, Modal, Checkbox, Select, ScrollArea, Divider, Box, SimpleGrid, ActionIcon, Tooltip } from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconCalendar, IconFilter, IconX, IconHelp, IconClock, IconInfoCircle } from "@tabler/icons-react";
import { useNavigate } from 'react-router-dom';
import { modals } from '@mantine/modals';
import ReactDOM from 'react-dom';
import ExcelJS from 'exceljs'
import CampaignReport from "../../api/CampaignReport";
import { notifications } from '@mantine/notifications';
import { VisiLoader, SelectTooltip, useColorScheme } from "../../components/GeneralComponent/GeneralComponent";
import { ReportVelocityDrawer } from "./ReportVelocityDrawer";

export function LifecycleAnalysisReport({
    selectedMemberWebsite,
    showLifecycleModal,
    setShowLifecycleModal,
    lifecycleCampaignId,
    lifecycleTrafficData,
    lifecycleReportId,
    selectedAttributionModal
}: any) {
    const navigate = useNavigate();
    const colorTheme = useColorScheme();
    const [loading, setLoading] = useState(false);
    const [showScopePopup, setShowScopePopup] = useState(false);
    const [totalColumns, setTotalColumns] = useState(0);
    const [campaignSources, setCampaignSources] = useState([]);
    const [selectedSources, setSelectedSources] = useState([]);
    const [changeScope, setChangeScope] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        key: "",
        direction: "",
    });
    const [expandedDrilldown, setExpandedDrilldown] = useState<{
        date: string;
        goal: string;
    } | null>(null);
    const [adDrilldownData, setAdDrilldownData] = useState([]);
    const [weekdayWeekendSummary, setWeekdayWeekendSummary] = useState([]);
    const [drilldownGoalName, setDrilldownGoalName] = useState("");
    const [weekdayWeekendToggle, setWeekdayWeekendToggle] = useState("all");
    const [campaignName, setCampaignName] = useState("");
    const [lpPageName, setLpPageName] = useState("");
    const [attributionModel, setAttributionModel] = useState("First Click");
    const [dateRange, setDateRange] = useState("");
    const [campaignGoals, setCampaignGoals] = useState([]);
    const [dailyCohorts, setDailyCohorts] = useState([]);
    const [summaries, setSummaries] = useState([]);
    const [dailyCohortDrilldownData, setDailyCohortDrilldownData] = useState([]);
    const [summariesDrilldownData, setSummariesDrilldownData] = useState([]);
    const analysisIconRef = useRef(null);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [helpTargetId, setHelpTargetId] = useState(0);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [showVelocityDrawer, setShowVelocityDrawer] = useState(false);
    const [velocityParams, setVelocityParams] = useState({});

    useEffect(() => {
        if(selectedAttributionModal !== ''){
            /*Set first click when full impact in selected in Main Report because we don't show full impact attribution in lifecycle*/
            const attribution = selectedAttributionModal === "Full Impact" ? "First Click" : selectedAttributionModal;
            setAttributionModel(attribution);
        }
    }, [selectedAttributionModal]);

    const getLifecycleReportData = (isChangeScope:any) => {
        const lifecycleSelectedSources = Object.entries(lifecycleTrafficData)
            .map(([key, value]: any) => ({
                traffic_name: key,
                level: value.level,
                data: value.data
        }));
        setLoading(true);
        const dataObject = {
            system_website_id: selectedMemberWebsite,
            campaign_id: lifecycleCampaignId,
            report_id: lifecycleReportId,
            attribution_model: attributionModel
        } as any;
        if(isChangeScope){
            dataObject.selectedSources = selectedSources
        }
        if(lifecycleSelectedSources && lifecycleSelectedSources.length > 0){
            dataObject.lifecycleSelectedSources = lifecycleSelectedSources
        }
        setExpandedDrilldown(null);
        setAdDrilldownData([]);
        setDailyCohortDrilldownData([]);
        setSummariesDrilldownData([]);
        CampaignReport.getlifecycleanalysisdata(dataObject)
        .then((response) => {
            if (response.ok) {
                return response.json();
            } else {
                if (response.status === 401 || response.status === 400) {
                    navigate('/logout');
                    setLoading(false);
                }
            }
        })
        .then((data) => {
            if(data.status === 1){
                setCampaignName(data.data.campaign_name);
                setLpPageName(data.data.lp_page_name);
                setCampaignSources(data.data.ads_array);
                setSelectedSources(data.data.selected_traffic_sources);
                setAttributionModel(data.data.attribution_model || "First Click");
                setDateRange(data.data.selected_time || "");
                setCampaignGoals(data.data.campaign_goals);
                setDailyCohorts(data.data.daily_report_data);
                setSummaries(data.data.summaries);
                setWeekdayWeekendSummary(data.data.weekday_weekend_summaries);
                setDailyCohortDrilldownData(data.data.daily_cohort_drilldown);
                setSummariesDrilldownData(data.data.cohort_drilldown);
                const goalsCount = Object.keys(data.data.campaign_goals).length;
                const columns = 3 + goalsCount * 4 + Math.max(0, goalsCount - 1) + 2;
                setTotalColumns(columns);
                setLoading(false);
            }else{
                setLoading(false);
                notifications.show({
                    color: 'red',
                    icon: <IconX size="1.1rem" />,
                    title: 'Error',
                    message: data.message,
                });
            }
        })
        .catch((error) => {
            console.error('API Error:', error);
            setLoading(false);
        });
    };

    const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    useEffect(() => {
        if(showLifecycleModal && lifecycleCampaignId !== "" && lifecycleReportId !== ""){
            getLifecycleReportData(changeScope);
        }
    }, [showLifecycleModal, lifecycleCampaignId, lifecycleReportId, attributionModel]);

    const handleChangeScope = (source: string, checked: boolean) => {
        setSelectedSources((prev) => {
            if (checked) {
                return [...prev, source];
            } else {
                return prev.filter((s) => s !== source);
            }
        });
    };

    const handleContinue = () => {
        setShowScopePopup(false);
        if(selectedSources.length === 0){
            modals.open({
                trapFocus: false,
                title: 'Action Required',
                children: (
                <>
                    <Text size="sm" style={{ whiteSpace: "pre-line" }}>
                        Please select at least one traffic source which you want of data to display.
                    </Text>
                    <Group position="right" mt="sm">
                        <Button
                            onClick={() => {
                                modals.closeAll();
                                setSelectedSources(campaignSources);
                                setShowScopePopup(true);
                            }}
                        >
                            OK
                        </Button>
                    </Group>
                </>
                ),
            });
            return;
        }
        setChangeScope(true);
        getLifecycleReportData(true);
    };

    const handleDrilldown = (date: string, goal: string, goalName: string, key: string) => {
        setDrilldownGoalName(goalName);
        setExpandedDrilldown((prev) =>
            prev?.date === date && prev?.goal === goal
                ? null
                : { date, goal }
        );
        const data = key === 'summary' ? summariesDrilldownData : dailyCohortDrilldownData;
        const drilldownData = data?.[date]?.[goal] ?? [];
        const rows = Array.isArray(drilldownData) ? drilldownData : [drilldownData];
        setAdDrilldownData(rows);
    };

    const handleSort = (key) => {
        setSortConfig({
            key,
            direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
        });
    };

    type CohortRow = {
        uniques: number;
        spend: number | string;
        [key: string]: number | string | null;
    };

    const sortedCohorts = useMemo<Record<string, CohortRow>>(() => {
        const cohortsArray = Object.entries(dailyCohorts).map(
            ([date, data]) => ({
                date,
                ...(data as CohortRow),
            })
        );
        cohortsArray.sort((a, b) => {
            if (!sortConfig.key) return 0;
            if (sortConfig.key === "date") {
                return sortConfig.direction === "asc"
                    ? new Date(a.date).getTime() - new Date(b.date).getTime()
                    : new Date(b.date).getTime() - new Date(a.date).getTime();
            }
            const aVal = Number(a[sortConfig.key]) || 0;
            const bVal = Number(b[sortConfig.key]) || 0;
            return sortConfig.direction === "asc"
                ? aVal - bVal
                : bVal - aVal;
        });

        return cohortsArray.reduce<Record<string, CohortRow>>((acc, row) => {
            const { date, ...rest } = row;
            acc[date] = rest;
            return acc;
        }, {});
    }, [dailyCohorts, sortConfig]);

    const SortIcon = ({ columnKey }) => {
        const iconProps = { size: 12 };
        if (sortConfig.key !== columnKey) {
            return <IconChevronDown {...iconProps} opacity={0.3} />;
        }
        return sortConfig.direction === "asc" ? (
            <IconChevronUp {...iconProps} opacity={0.7} />
        ) : (
            <IconChevronDown {...iconProps} opacity={0.7} />
        );
    };

    const handleHelpClick = (id:any) => {
        setShowHelpModal(true);
        setHelpTargetId(id);
        if (analysisIconRef.current) {
        const { top, left, width } = analysisIconRef.current.getBoundingClientRect();
        const scrollOffset = window.scrollY;
        setTooltipPosition({
            top: top + scrollOffset,
            left: left + width + 10, 
        });
        }
    };

    const handleOpenVelocityDrawer = (goalData: any, cohort: any) => {
        const lifecycleSelectedSources = Object.entries(lifecycleTrafficData)
            .map(([key, value]: any) => ({
                traffic_name: key,
                level: value.level,
                data: value.data
            }));
        const params: any = {
            system_website_id: selectedMemberWebsite,
            campaign_id: lifecycleCampaignId,
            report_id: lifecycleReportId,
            campaign_goal_id: goalData.goal_id,
            goal_type: goalData.goal_type,
            attribution_model: attributionModel,
            selectedSources: selectedSources,
            cohort_type: cohort.cohort_type,
        };
        if (lifecycleSelectedSources && lifecycleSelectedSources.length > 0) {
            params.lifecycleSelectedSources = lifecycleSelectedSources;
        }
        if (cohort.cohort_type === 'daily') {
            params.cohort_date = cohort.cohort_date;
        } else {
            params.cohort_start = cohort.cohort_start;
            params.cohort_end = cohort.cohort_end;
        }
        setVelocityParams(params);
        setShowVelocityDrawer(true);
    };

    const exportToExcel = async (): Promise<void> => {
        const ExcelJS = (await import('exceljs')).default;
        const wb = new ExcelJS.Workbook();

        const C = {
            navyBg:      'FF4A90D9',  // column headers
            navyFont:    'FFFFFFFF',  // white
            metaBg:      'FFE8F4FD',  // meta rows
            metaLabel:   'FF2C5F8A',  // meta label text
            summaryBg:   'FFB8D4F0',  // summary data rows
            summaryFont: 'FF1A3A5C',  // readable on light bg
            summaryHdr:  'FF6AAED6',  // COHORT SUMMARIES banner
            dailyEven:   'FFF0F7FF',  // daily rows (even)
            dailyOdd:    'FFFAFCFF',  // daily rows (odd)
            adEven:      'FFF2F7FB',
            adOdd:       'FFFFFFFF',
            border:      'FFB8CCE4',
        };

        const bd: ExcelJS.Border = { style: 'thin', color: { argb: C.border } };
        const borders = { top: bd, bottom: bd, left: bd, right: bd };

        const fill = (argb: string): ExcelJS.Fill =>
            ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

        const fnt = (bold: boolean, argb: string, size = 10): Partial<ExcelJS.Font> =>
            ({ name: 'Arial', size, bold, color: { argb } });

        const styleCell = (
            cell: ExcelJS.Cell,
            value: any,
            fillArgb: string,
            fontArgb: string,
            bold = false,
            numFmt?: string,
            align: ExcelJS.Alignment['horizontal'] = 'left',
        ) => {
            cell.value     = value ?? null;
            cell.fill      = fill(fillArgb);
            cell.font      = fnt(bold, fontArgb);
            cell.border    = borders;
            cell.alignment = { horizontal: align, vertical: 'middle' };
            if (numFmt) cell.numFmt = numFmt;
        };

        // =========================================================================
        // SHEET 1 – Lifecycle Cohorts
        // =========================================================================
        const s1 = wb.addWorksheet('Lifecycle Cohorts');
        s1.views = [{ state: 'frozen', xSplit: 0, ySplit: 5 }];

        // Meta rows 1–4
        const meta = [
            ['Campaign',          campaignName],
            ['Attribution Model', attributionModel],
            ['Traffic Sources',   selectedSources?.length ? selectedSources.join(', ') : ''],
            ['Date Range',        dateRange],
        ];
        meta.forEach((m, i) => {
            const r = s1.getRow(i + 1);
            styleCell(r.getCell(1), m[0], C.metaBg, C.metaLabel, true);
            styleCell(r.getCell(2), m[1], C.metaBg, C.metaLabel, false);
            r.height = 18;
            r.commit();
        });

        type Col = {
            header: string;
            key: string;
            width: number;
            fmt?: string;
            align?: ExcelJS.Alignment['horizontal'];
        };

        const fixedCols: Col[] = [
            { header: 'Row Type',          key: 'rowType',   width: 12 },
            { header: 'Cohort Entry Date', key: 'entryDate', width: 20 },
            { header: 'Cohort Window',     key: 'window',    width: 22 },
            { header: 'Spend',             key: 'spend',     width: 14, fmt: '"$"#,##0.00', align: 'right' },
            { header: 'Uniques',           key: 'uniques',   width: 12, fmt: '#,##0',       align: 'right' },
        ];

        const goalCols: Col[] = [];
        Object.entries(campaignGoals).forEach(([goalKey, goalData]: any, gi) => {
            goalCols.push({ header: `${goalData.name}\nConversions`,  key: `${goalKey}_conversion`, width: 16, fmt: '#,##0',       align: 'right' });
            if (gi > 0)
                goalCols.push({ header: `${goalData.name}\n% of Prev`, key: `${goalKey}_perc_prev`, width: 14, fmt: '0.00%',       align: 'right' });
            goalCols.push({ header: `${goalData.name}\nConv %`,       key: `${goalKey}_conv_perc`,  width: 14, fmt: '0.00%',       align: 'right' });
            goalCols.push({ header: `${goalData.name}\nCP Goal`,      key: `${goalKey}_cp_goal`,    width: 14, fmt: '"$"#,##0.00', align: 'right' });
            goalCols.push({ header: `${goalData.name}\nAvg Days`,     key: `${goalKey}_avg_days`,   width: 13, fmt: '0.0',         align: 'right' });
        });

        const tailCols: Col[] = [
            { header: 'LTV\n(Rev/Entry)', key: 'ltv',  width: 16, fmt: '"$"#,##0.00', align: 'right' },
            { header: 'ROAS',             key: 'roas', width: 10, fmt: '0.00"x"',     align: 'right' },
        ];

        const allCols = [...fixedCols, ...goalCols, ...tailCols];
        s1.columns = allCols.map(c => ({ key: c.key, width: c.width }));

        const writeS1Header = (rowNum: number) => {
            const r = s1.getRow(rowNum);
            allCols.forEach((c, ci) => {
                const cell = r.getCell(ci + 1);
                styleCell(cell, c.header, C.navyBg, C.navyFont, true, undefined, 'center');
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            });
            r.height = 36;
            r.commit();
        };
        writeS1Header(5);

        const cohortValues = (rowType: 'Daily' | 'Summary', date: string, window: string, data: any) => {
            const v: Record<string, any> = {
                rowType,
                entryDate: date,
                window,
                spend:   typeof data.spend === 'number' ? data.spend : null,
                uniques: data.uniques ?? null,
            };
            Object.entries(campaignGoals).forEach(([goalKey, _]: any, gi) => {
                v[`${goalKey}_conversion`] = data[`${goalKey}_conversion`] ?? 0;
                if (gi > 0) {
                    const p = data[`${goalKey}_perc_prev`];
                    v[`${goalKey}_perc_prev`] = typeof p === 'number' ? p / 100 : null;
                }
                const cp = data[`${goalKey}_conversion_perc`];
                v[`${goalKey}_conv_perc`] = typeof cp === 'number' ? cp / 100 : null;
                const cpg = data[`${goalKey}_cp_goal`];
                v[`${goalKey}_cp_goal`] = typeof cpg === 'number' ? cpg : null;
                const avg = data[`${goalKey}_avg_days`];
                v[`${goalKey}_avg_days`] = typeof avg === 'number' ? avg : null;
            });
            v.ltv  = typeof data.revenue_per_entry === 'number' ? data.revenue_per_entry : null;
            v.roas = typeof data.roas              === 'number' ? data.roas               : null;
            return v;
        };

        let s1Row   = 6;
        let altFlag = false;

        const writeCohortRow = (rowType: 'Daily' | 'Summary', date: string, window: string, data: any) => {
            const r      = s1.getRow(s1Row++);
            const bgArgb = altFlag ? C.dailyOdd : C.dailyEven;
            const fgArgb = 'FF1A1A1A';
            altFlag = !altFlag;

            const v = cohortValues(rowType, date, window, data);
            allCols.forEach((c, ci) => {
                styleCell(r.getCell(ci + 1), v[c.key] ?? null, bgArgb, fgArgb, false, c.fmt, c.align ?? 'left');
            });
            r.height = 18;
            r.commit();
        };

        Object.entries(displayCohorts).forEach(([date, data]: any) => {
            writeCohortRow('Daily', date, '', data);
        });

        // Blank spacer
        s1.getRow(s1Row++).commit();

        // COHORT SUMMARIES banner
        const banner = s1.getRow(s1Row++);
        const bc     = banner.getCell(1);
        bc.value     = 'COHORT SUMMARIES';
        bc.fill      = fill(C.summaryHdr);
        bc.font      = fnt(true, C.navyFont, 11);
        bc.border    = borders;
        bc.alignment = { horizontal: 'left', vertical: 'middle' };
        s1.mergeCells(s1Row - 1, 1, s1Row - 1, allCols.length);
        banner.height = 22;
        banner.commit();

        writeS1Header(s1Row++);

        altFlag = false;
        Object.entries(displaySummaries).forEach(([date, data]: any) => {
            writeCohortRow('Summary', date, data.summary_key ?? date, data);
        });

        // =========================================================================
        // SHEET 2 – Ad Drilldown (colors unchanged)
        // =========================================================================
        const s2 = wb.addWorksheet('Ad Drilldown');
        s2.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

        const ddCols: Col[] = [
            { header: 'Cohort Entry Date', key: 'cohortDate',  width: 20 },
            { header: 'Goal Name',         key: 'goalName',    width: 22 },
            { header: 'Attribution Model', key: 'attribution', width: 24 },
            { header: 'Ad Name',           key: 'adName',      width: 36 },
            { header: 'Spend',             key: 'spend',       width: 14, fmt: '"$"#,##0.00', align: 'right' },
            { header: 'Entries',           key: 'entries',     width: 12, fmt: '#,##0',       align: 'right' },
            { header: 'Cost Per Goal',     key: 'cpGoal',      width: 16, fmt: '"$"#,##0.00', align: 'right' },
        ];

        s2.columns = ddCols.map(c => ({ key: c.key, width: c.width }));

        const ddHdrRow = s2.getRow(1);
        ddCols.forEach((c, ci) => {
            styleCell(ddHdrRow.getCell(ci + 1), c.header, C.navyBg, C.navyFont, true, undefined, 'center');
        });
        ddHdrRow.height = 28;
        ddHdrRow.commit();

        let s2Row = 2;
        let ddAlt = false;

        const writeAdRow = (cohortDate: string, goalName: string, ad: any) => {
            const r      = s2.getRow(s2Row++);
            const bgArgb = ddAlt ? C.adOdd : C.adEven;
            ddAlt = !ddAlt;

            const v: Record<string, any> = {
                cohortDate,
                goalName,
                attribution: attributionModel,
                adName:      ad.ad_name,
                spend:       typeof ad.spend   === 'number' ? ad.spend   : null,
                entries:     ad.entries ?? null,
                cpGoal:      typeof ad.cp_goal === 'number' && ad.cp_goal > 0 ? ad.cp_goal : null,
            };

            ddCols.forEach((c, ci) => {
                styleCell(r.getCell(ci + 1), v[c.key] ?? null, bgArgb, 'FF000000', false, c.fmt, c.align ?? 'left');
            });
            r.height = 18;
            r.commit();
        };

        Object.entries(displayCohorts).forEach(([date, _]: any) => {
            Object.entries(campaignGoals).forEach(([goalKey, goalData]: any) => {
                const dd = dailyCohortDrilldownData[date]?.[goalKey];
                if (dd) {
                    const ads = Array.isArray(dd) ? dd : [dd];
                    ads.forEach(ad => writeAdRow(date, goalData.name, ad));
                }
            });
        });

        Object.entries(displaySummaries).forEach(([date, data]: any) => {
            const window = data.summary_key ?? date;
            Object.entries(campaignGoals).forEach(([goalKey, goalData]: any) => {
                const dd = summariesDrilldownData[date]?.[goalKey];
                if (dd) {
                    const ads = Array.isArray(dd) ? dd : [dd];
                    ads.forEach(ad => writeAdRow(window, goalData.name, ad));
                }
            });
        });

        const buffer = await wb.xlsx.writeBuffer();
        const blob   = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'lifecycle-report.xlsx';
        link.click();
        URL.revokeObjectURL(url);
    };

    const displayCohorts = weekdayWeekendToggle === "grouped" ? [] : sortedCohorts;
    const displaySummaries = weekdayWeekendToggle === "grouped" ? weekdayWeekendSummary : summaries;

    return (
    <>
        {showHelpModal &&
            ReactDOM.createPortal(
            <div>
                <SelectTooltip
                    isVisible={showHelpModal}
                    setIsVisible={setShowHelpModal}
                    id={helpTargetId}
                    tooltipPosition={tooltipPosition} 
                />
            </div>,
            document.body
        )}
        <Modal.Root
            opened={showScopePopup}
            onClose={() => setShowScopePopup(false)}
            size="lg"
            withinPortal
            zIndex={1100}
        >
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>Lifecycle Analysis Scope</Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <Card withBorder mb="md" bg="blue.0">
                        <Text size="sm" fw={600}>Select which traffic sources to use for your Lifecycle Analysis.</Text>
                        <Text size="sm" c="dimmed" mt={4}>Whatever view you have in traffic reports will be used.</Text>
                    </Card>
                    {campaignSources.map((s) => (
                        <Checkbox
                            key={s}
                            label={s}
                            checked={selectedSources.includes(s)}
                            onChange={(e) => handleChangeScope(s, e.currentTarget.checked)}
                            mb={8}
                        />
                    ))}
                    <Group position="right" mt="md">
                        <Button variant="default" onClick={() => setShowScopePopup(false)}>Cancel</Button>
                        <Button onClick={handleContinue}>Continue</Button>
                    </Group>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
        <Modal.Root
            opened={showLifecycleModal}
            onClose={() => setShowLifecycleModal(false)}
            fullScreen
            styles={{
                content: { padding: 0 },
            }}
        >
            <Modal.Overlay />
            <Modal.Content sx={{ overflowX: "hidden" }}>
                <VisiLoader VisiLoaderVisible={loading} />
                <Modal.Body>
                    <Card withBorder radius="md" p="lg" mb="md">
                    <Group position="apart" mb="md">
                        <Text weight={600}>
                            Lifecycle Analysis <IconHelp ref={analysisIconRef} size="1.1rem" color="red" style={{marginBottom:-3, cursor: 'pointer'}} onClick={() => handleHelpClick(362)}/>
                        </Text>
                        <Group spacing="xs">
                            <Button
                                size="xs"
                                variant="filled"
                                disabled={loading}
                                onClick={() => exportToExcel()}
                            >
                                Export Lifecycle Report
                            </Button>
                            
                            {campaignSources && campaignSources.length > 1 && (
                                <Button
                                    leftIcon={<IconFilter size={14} />}
                                    variant="default"
                                    onClick={() => setShowScopePopup(true)}
                                >
                                    Change Scope
                                </Button>
                            )}

                            <ActionIcon
                                variant="default"
                                size="lg"
                                onClick={() => setShowLifecycleModal(false)}
                            >
                                <IconX size={18} />
                            </ActionIcon>
                        </Group>
                    </Group>
                    <SimpleGrid cols={1} spacing="sm">
                        <Box>
                            <Text size="sm" color="dimmed">
                                Visiopt Campaign:
                            </Text>
                            <Text weight={600}>{campaignName}</Text>
                        </Box>
                        <Box>
                            <Text size="sm" color="dimmed">
                                LP Page Name:
                            </Text>
                            <Text weight={600}>{lpPageName}</Text>
                        </Box>
                        {selectedSources && selectedSources.length > 0 && (
                            <Box>
                                <Text size="sm" color="dimmed">
                                    Traffic Sources:
                                </Text>
                                <Text weight={600}>{selectedSources.join(", ")}</Text>
                            </Box>
                        )}
                        <Box>
                        <Text size="sm" color="dimmed">
                            Attribution Model:
                        </Text>
                        <Select
                            value={attributionModel}
                            onChange={setAttributionModel}
                            data={["First Click", "Last Click", "Same Day"]}
                        />
                        </Box>
                        <Box>
                        <Text size="sm" color="dimmed">
                            Cohort Entry Window:
                        </Text>
                        <Group spacing={6}>
                            <IconCalendar size={14} />
                            <Text size="sm">{dateRange}</Text>
                        </Group>
                        </Box>
                    </SimpleGrid>
                    <Divider my="md" />
                    <Group spacing="xs">
                        <Text size="sm">Grouping:</Text>
                        <Button
                        size="xs"
                        variant={
                            weekdayWeekendToggle === "all" ? "filled" : "default"
                        }
                        onClick={() => setWeekdayWeekendToggle("all")}
                        >
                        Daily Cohorts
                        </Button>
                        <Button
                        size="xs"
                        variant={
                            weekdayWeekendToggle === "grouped" ? "filled" : "default"
                        }
                        onClick={() => setWeekdayWeekendToggle("grouped")}
                        >
                        Weekday vs Weekend
                        </Button>
                    </Group>
                    </Card>
                    <ScrollArea type="always" scrollbarSize={8} offsetScrollbars>
                        <Table highlightOnHover withBorder
                            sx={{ tableLayout: "fixed", minWidth: "1500",
                                "& th, & td": {
                                    padding: "10px 14px",
                                    whiteSpace: "nowrap",
                                    fontSize: 14,
                                    textAlign: "center"
                                },
                            }}
                        >
                            <colgroup>
                                <col style={{ width: 150 }} />
                                <col style={{ width: 80 }} />
                                <col style={{ width: 80 }} />

                                {Object.entries(campaignGoals).map(([goalIndex, goalData], index) => {
                                    return (
                                        <>
                                            <col style={{ width: 90 }} />
                                            <col style={{ width: 110 }} />
                                            <col style={{ width: 100 }} />
                                            <col style={{ width: 80 }} />
                                            {index > 0 && <col style={{ width: 100 }} />} 
                                        </>
                                    );
                                })}

                                <col style={{ width: 130 }} />
                                <col style={{ width: 90 }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th style={{ whiteSpace: "normal" }}>
                                        <button style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }} onClick={() => handleSort("date")}>
                                            <span>Entry Date</span>
                                            <SortIcon columnKey="date" />
                                        </button>
                                    </th>
                                    <th>
                                        <button style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }} onClick={() => handleSort("spend")}>
                                            <Tooltip multiline width={260} withArrow withinPortal events={{ hover: true, focus: true, touch: true }} label="Spend on the ads that brought converting customers. This can be lower than total spend elsewhere in the report.">
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "help" }}>Spend <IconInfoCircle size="0.8rem" /></span>
                                            </Tooltip>
                                            <SortIcon columnKey="spend" />
                                        </button>
                                    </th>
                                    <th>
                                        <button style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }} onClick={() => handleSort("entries")}>
                                            <span>Uniques</span>
                                            <SortIcon columnKey="entries" />
                                        </button>
                                    </th>
                                    {Object.entries(campaignGoals).map(([goalKey, goalData], goalIndex) => {
                                        const bgColor = colorTheme === "light" ? goalData.color : hexToRgba(goalData.color, 0.3);
                                        return (
                                            <th key={goalKey} colSpan={goalIndex > 0 ? 5 : 4} style={{ background: bgColor, textAlign: "center", color: colorTheme !== "light" ? "#fff" : "#000" }}>{goalData.name}</th>
                                        );
                                    })}
                                    <th colSpan={2} style={{ background: colorTheme === "light" ? '#FFF1F2' : hexToRgba('#FFF1F2', 0.3), textAlign: "center" }}>Revenue</th>
                                </tr>
                                <tr>
                                    <th />
                                    <th />
                                    <th />
                                    {Object.entries(campaignGoals).map(([goalKey, goalData], goalIndex) => {
                                        const headers = ["Conversions"];
                                        if (goalIndex > 0) headers.push("% of Previous");
                                        headers.push("Conversion %", "Cost Per Goal", "Avg Days");
                                        const bgColor = colorTheme === "light" ? goalData.color : hexToRgba(goalData.color, 0.3);
                                        return headers.map((h) => (
                                            <th
                                                key={`${goalKey}-${h}`}
                                                style={{
                                                    background: bgColor,
                                                    textAlign: "center",
                                                    fontWeight: 600,
                                                    fontSize: 12,
                                                    whiteSpace: "normal",
                                                    color: colorTheme !== "light" ? "#fff" : "#000"
                                                }}
                                            >
                                                {h}
                                            </th>
                                        ));
                                    })}
                                    <th style={{ background: colorTheme === "light" ? '#FFF1F2' : hexToRgba('#FFF1F2', 0.3), textAlign: "right", whiteSpace: "normal", fontSize: 12, fontWeight: 600 }}>
                                        Revenue per Entry (LTV)
                                    </th>
                                    <th style={{ background: colorTheme === "light" ? '#FFF1F2' : hexToRgba('#FFF1F2', 0.3), textAlign: "center", whiteSpace: "normal", fontSize: 12, fontWeight: 600 }}>
                                        ROAS
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                            {Object.entries(displayCohorts).map(([date, data], index) => (
                                <React.Fragment key={date}>
                                <tr>
                                    <td style={{ fontWeight: 600, textAlign: "left" }}>{date}</td>
                                    <td>
                                        ${data.spend.toLocaleString()}
                                    </td>
                                    <td>{data.uniques}</td>
                                    {Object.entries(campaignGoals).map(([goalKey, goalData], goalIndex) => {
                                        const bgColor = colorTheme === "light" ? goalData.color : hexToRgba(goalData.color, 0.3);
                                        const conversionKey = `${goalKey}_conversion`;
                                        const conversionPercKey = `${goalKey}_conversion_perc`;
                                        const prevPercKey = `${goalKey}_perc_prev`;
                                        const cpGoalKey = `${goalKey}_cp_goal`;
                                        const avgKey = `${goalKey}_avg_days`;

                                        return (
                                            <>
                                                <td style={{ background: bgColor }}>
                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                                                        <span>{data[conversionKey] ?? 0}</span>
                                                        {Number(data[conversionKey]) > 0 && goalData.goal_id && (
                                                            <ActionIcon
                                                                size="sm"
                                                                variant="subtle"
                                                                title="Velocity Report"
                                                                onClick={() => handleOpenVelocityDrawer(goalData, { cohort_type: "daily", cohort_date: date })}
                                                            >
                                                                <IconClock size="1rem" />
                                                            </ActionIcon>
                                                        )}
                                                    </div>
                                                </td>
                                                {goalIndex > 0 && (
                                                    <td style={{ background: bgColor, textAlign: "center" }}>
                                                        {typeof data[prevPercKey] === "number" ? `${data[prevPercKey].toFixed(2)}%` : "-"}
                                                    </td>
                                                )}
                                                <td style={{ background: bgColor }} >
                                                    {typeof data[conversionPercKey] === "number" ? `${data[conversionPercKey].toFixed(2)}%` : "-"}
                                                </td>
                                                <td style={{ background: bgColor }} >
                                                    {data[conversionKey] && data[conversionKey] > 0 ? (
                                                        <Button
                                                            variant="subtle"
                                                            size="xs"
                                                            onClick={() => handleDrilldown(date, goalKey, goalData.name, 'daily')}
                                                        >
                                                            {typeof data[cpGoalKey] === "number" ? data[cpGoalKey].toFixed(2) : "-"}
                                                        </Button>
                                                    ) : (
                                                        typeof data[cpGoalKey] === "number" ? data[cpGoalKey].toFixed(2) : "-"
                                                    )}
                                                    
                                                </td>
                                                <td style={{ background: bgColor, textAlign: "center" }}>
                                                    {typeof data[avgKey] === "number" ? data[avgKey].toFixed(1) : "-"}
                                                </td>
                                            </>
                                        );
                                    })}
                                    <td style={{ background: colorTheme === "light" ? '#FFF1F2' : hexToRgba('#FFF1F2', 0.3), textAlign: "center", fontWeight: 600 }}>
                                        {typeof data.revenue_per_entry === "number" ? `${data.revenue_per_entry.toFixed(2)}` : "-"}
                                    </td>
                                    <td style={{ background: colorTheme === "light" ? '#FFF1F2' : hexToRgba('#FFF1F2', 0.3), textAlign: "center", fontWeight: 600 }}>
                                        {typeof data.roas === "number" ? `${data.roas.toFixed(2)}x` : "-"}
                                    </td>
                                </tr>
                                {expandedDrilldown?.date === date && (
                                    <tr>
                                        <td colSpan={totalColumns} style={{ padding: 0 }}>
                                            <Box p={0} sx={{ background: colorTheme === "light" ? "#edf5ff" : "333436", borderTop: "1px solid #dbe4ff" }}>
                                                <Text size="sm" fw={600} c={colorTheme === "light" ? "gray.7" : "#dce2e8"} mb={8} mt={8}>
                                                    {`${drilldownGoalName} – Ad-Level CP Goal Breakdown`}
                                                </Text>
                                                <Table withBorder
                                                    sx={{
                                                        width: "100%",
                                                        tableLayout: "fixed",
                                                        fontSize: 12,
                                                        "& th, & td": {
                                                            padding: "8px 10px",
                                                        },
                                                        "& th": {
                                                            fontWeight: 500,
                                                            color: "#495057",
                                                            borderBottom: "1px solid #dbe4ff",
                                                        },
                                                        "& td": {
                                                            borderBottom: "1px solid #e7f5ff",
                                                        },
                                                    }}
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th style={{ textAlign: "left", fontSize: 12, fontWeight: 500 }}>
                                                                Ad Name
                                                            </th>
                                                            <th style={{ textAlign: "right", fontSize: 12, fontWeight: 500 }}>
                                                                Spend
                                                            </th>
                                                            <th style={{ textAlign: "right", fontSize: 12, fontWeight: 500 }}>
                                                                Entries
                                                            </th>
                                                            <th style={{ textAlign: "right", fontSize: 12, fontWeight: 500 }}>
                                                                CP Goal
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {adDrilldownData.map(
                                                            (ad, adIdx) => (
                                                                <tr key={adIdx}>
                                                                    <td style={{ textAlign: "left", width: 200 }}>{ad.ad_name}</td>
                                                                    <td style={{ textAlign: "right" }}>
                                                                        ${ad.spend.toLocaleString()}
                                                                    </td>
                                                                    <td style={{ textAlign: "right" }}>
                                                                        {ad.entries}
                                                                    </td>
                                                                    <td style={{ textAlign: "right" }}>
                                                                        {ad.cp_goal > 0 ? `$${ad.cp_goal.toFixed(2)}` : "—"}
                                                                    </td>
                                                                </tr>
                                                            )
                                                        )}
                                                    </tbody>
                                                    {adDrilldownData.length > 0 && (
                                                        <tfoot>
                                                            <tr style={{ fontWeight: 600 }}>
                                                                <td style={{ textAlign: "left" }}>Total</td>
                                                                <td style={{ textAlign: "right" }}>${adDrilldownData.reduce((t, ad) => t + (Number(ad.spend) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                                <td style={{ textAlign: "right" }}>{adDrilldownData.reduce((t, ad) => t + (Number(ad.entries) || 0), 0)}</td>
                                                                <td />
                                                            </tr>
                                                        </tfoot>
                                                    )}
                                                </Table>
                                            </Box>
                                        </td>
                                    </tr>
                                )}

                                </React.Fragment>
                            ))}
                            {Object.keys(displaySummaries).length > 0 && (
                                <>
                                <tr>
                                    <td style={{ background: colorTheme === 'light' ? "#f8f9fa" : 'transparent', fontWeight: 600, padding: "12px 14px", borderTop: colorTheme === 'light' ? "2px solid #dee2e6" : "none", color: colorTheme === 'light' ? "#000" : "#e5e7eb" }}>
                                        Cohort Summaries
                                    </td>
                                </tr>
                                {Object.entries(displaySummaries).map(([date, data], index) => (
                                    <>
                                        <tr key={index}>
                                            <td style={{ padding: "10px 14px", fontWeight: 600, color: "#1c7ed6", whiteSpace: "normal" }}>
                                                {data.summary_key}
                                            </td>
                                            <td style={{ padding: "10px 14px", fontWeight: 600, }}>
                                                ${data.spend.toLocaleString()}
                                            </td>
                                            <td style={{ padding: "10px 14px", fontWeight: 600 }}>
                                                {data.uniques}
                                            </td>
                                            {Object.entries(campaignGoals).map(([goalKey, goalData], goalIndex) => {
                                                const bgColor = colorTheme === "light" ? goalData.color : hexToRgba(goalData.color, 0.3);
                                                const conversionKey = `${goalKey}_conversion`;
                                                const conversionPercKey = `${goalKey}_conversion_perc`;
                                                const cpGoalKey = `${goalKey}_cp_goal`;
                                                const avgKey = `${goalKey}_avg_days`;
                                                const prevPercKey = `${goalKey}_perc_prev`;
                                                return (
                                                    <>
                                                        <td style={{ background: bgColor }}>
                                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                                                                <span>{data[conversionKey] ?? 0}</span>
                                                                {Number(data[conversionKey]) > 0 && goalData.goal_id && data.cohort_type && (
                                                                    <ActionIcon
                                                                        size="sm"
                                                                        variant="subtle"
                                                                        title="Velocity Report"
                                                                        onClick={() => handleOpenVelocityDrawer(goalData, { cohort_type: data.cohort_type, cohort_start: data.cohort_start, cohort_end: data.cohort_end })}
                                                                    >
                                                                        <IconClock size="1rem" />
                                                                    </ActionIcon>
                                                                )}
                                                            </div>
                                                        </td>
                                                        {goalIndex > 0 && (
                                                            <td style={{ background: bgColor, textAlign: "center" }}>
                                                                {typeof data[prevPercKey] === "number" ? `${data[prevPercKey].toFixed(2)}%` : "-"}
                                                            </td>
                                                        )}
                                                        <td style={{ background: bgColor }} >
                                                            {typeof data[conversionPercKey] === "number" ? `${data[conversionPercKey].toFixed(2)}%` : "-"}
                                                        </td>
                                                        <td style={{ background: bgColor }} >
                                                            <Button
                                                                variant="subtle"
                                                                size="xs"
                                                                onClick={() => handleDrilldown(date, goalKey, goalData.name, 'summary')}
                                                            >
                                                                {typeof data[cpGoalKey] === "number" ? data[cpGoalKey].toFixed(2) : "-"}
                                                            </Button>
                                                        </td>
                                                        <td style={{ background: bgColor, textAlign: "right" }}>
                                                            {typeof data[avgKey] === "number" ? data[avgKey].toFixed(1) : "-"}
                                                        </td>
                                                    </>
                                                );
                                            })}
                                            <td style={{ background: colorTheme === "light" ? '#FFF1F2' : hexToRgba('#FFF1F2', 0.3), fontWeight: 700 }}>
                                                {typeof data.revenue_per_entry === "number" ? `${data.revenue_per_entry.toFixed(2)}` : "-"}
                                            </td>
                                            <td style={{ background: colorTheme === "light" ? '#FFF1F2' : hexToRgba('#FFF1F2', 0.3), fontWeight: 700 }}>
                                                {typeof data.roas === "number" ? `${data.roas.toFixed(2)}x` : "-"}
                                            </td>
                                        </tr>
                                        {expandedDrilldown?.date === date && (
                                            <tr>
                                                <td colSpan={totalColumns} style={{ padding: 0 }}>
                                                    <Box p={0} sx={{ background: colorTheme === "light" ? "#edf5ff" : "333436", borderTop: "1px solid #dbe4ff" }}>
                                                        <Text size="sm" fw={600} c={colorTheme === "light" ? "gray.7" : "#dce2e8"} mb={8} mt={8}>
                                                            {`${drilldownGoalName} – Ad-Level CP Goal Breakdown`}
                                                        </Text>
                                                        <Table withBorder
                                                            sx={{
                                                                width: "100%",
                                                                tableLayout: "fixed",
                                                                fontSize: 12,
                                                                "& th, & td": {
                                                                    padding: "8px 10px",
                                                                },
                                                                "& th": {
                                                                    fontWeight: 500,
                                                                    color: "#495057",
                                                                    borderBottom: "1px solid #dbe4ff",
                                                                },
                                                                "& td": {
                                                                    borderBottom: "1px solid #e7f5ff",
                                                                },
                                                            }}
                                                        >
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ textAlign: "left", fontSize: 12, fontWeight: 500 }}>
                                                                        Ad Name
                                                                    </th>
                                                                    <th style={{ textAlign: "right", fontSize: 12, fontWeight: 500 }}>
                                                                        Spend
                                                                    </th>
                                                                    <th style={{ textAlign: "right", fontSize: 12, fontWeight: 500 }}>
                                                                        Entries
                                                                    </th>
                                                                    <th style={{ textAlign: "right", fontSize: 12, fontWeight: 500 }}>
                                                                        CP Goal
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {adDrilldownData.map(
                                                                    (ad, adIdx) => (
                                                                        <tr key={adIdx}>
                                                                            <td style={{ textAlign: "left", width: 200 }}>{ad.ad_name}</td>
                                                                            <td style={{ textAlign: "right" }}>
                                                                                ${ad.spend.toLocaleString()}
                                                                            </td>
                                                                            <td style={{ textAlign: "right" }}>
                                                                                {ad.entries}
                                                                            </td>
                                                                            <td style={{ textAlign: "right" }}>
                                                                                {ad.cp_goal > 0 ? `$${ad.cp_goal.toFixed(2)}` : "—"}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                )}
                                                            </tbody>
                                                            {adDrilldownData.length > 0 && (
                                                                <tfoot>
                                                                    <tr style={{ fontWeight: 600 }}>
                                                                        <td style={{ textAlign: "left" }}>Total</td>
                                                                        <td style={{ textAlign: "right" }}>${adDrilldownData.reduce((t, ad) => t + (Number(ad.spend) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                                        <td style={{ textAlign: "right" }}>{adDrilldownData.reduce((t, ad) => t + (Number(ad.entries) || 0), 0)}</td>
                                                                        <td />
                                                                    </tr>
                                                                </tfoot>
                                                            )}
                                                        </Table>
                                                    </Box>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                ))}
                                </>
                            )}
                            </tbody>
                        </Table>
                    </ScrollArea>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
        {showVelocityDrawer && (
            <ReportVelocityDrawer
                report_id=""
                showVelocityDrawer={showVelocityDrawer}
                setShowVelocityDrawer={setShowVelocityDrawer}
                campaignGoalId=""
                encodeCampaignName=""
                selectedCampaignName=""
                adsetName=""
                trafficSource=""
                campaign_id=""
                trafficLevel=""
                goalType=""
                combinedGoalType={false}
                selectedAttributionModal={attributionModel}
                displayCampaignName={campaignName}
                velocityMode="lifecycle"
                lifecycleVelocityParams={velocityParams}
            />
        )}
    </>
  );
}

export default LifecycleAnalysisReport;